import { Smartphone, Check, Copy, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

/**
 * Authenticator App Setup Dialog
 * Handles QR code display, verification, and backup codes
 */
export function AuthenticatorSetupDialog({
  open,
  onOpenChange,
  setupStep,
  setSetupStep,
  setupData,
  verifyCode,
  setVerifyCode,
  backupCodes,
  actionLoading,
  error,
  copiedIndex,
  onVerifySetup,
  onCopyToClipboard,
  onCopyAllBackupCodes,
  onComplete
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Smartphone className="w-5 h-5 text-teal-600" />
            <span>Sett opp autentiseringsapp</span>
          </DialogTitle>
          <DialogDescription>
            {setupStep === 'qr' && 'Skann QR-koden med en autentiseringsapp'}
            {setupStep === 'verify' && 'Skriv inn koden fra appen din'}
            {setupStep === 'backup' && 'Lagre backup-kodene dine trygt'}
          </DialogDescription>
        </DialogHeader>

        {setupStep === 'qr' && setupData && (
          <QRCodeStep
            setupData={setupData}
            copiedIndex={copiedIndex}
            onCopyToClipboard={onCopyToClipboard}
            onClose={() => onOpenChange(false)}
            onNext={() => setSetupStep('verify')}
          />
        )}

        {setupStep === 'verify' && (
          <VerifyStep
            verifyCode={verifyCode}
            setVerifyCode={setVerifyCode}
            actionLoading={actionLoading}
            error={error}
            onBack={() => setSetupStep('qr')}
            onVerify={onVerifySetup}
          />
        )}

        {setupStep === 'backup' && (
          <BackupCodesStep
            backupCodes={backupCodes}
            copiedIndex={copiedIndex}
            onCopyToClipboard={onCopyToClipboard}
            onCopyAllBackupCodes={onCopyAllBackupCodes}
            onComplete={onComplete}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function QRCodeStep({ setupData, copiedIndex, onCopyToClipboard, onClose, onNext }) {
  return (
    <div className="space-y-4">
      <div className="flex justify-center">
        <img 
          src={setupData.qr_code} 
          alt="2FA QR Code" 
          className="w-48 h-48 border rounded-lg"
        />
      </div>
      <div className="text-center">
        <p className="text-sm text-slate-500 mb-2">Kan ikke skanne? Bruk denne koden:</p>
        <div className="flex items-center justify-center space-x-2">
          <code className="bg-slate-100 px-3 py-1 rounded text-sm font-mono">
            {setupData.manual_entry_key}
          </code>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onCopyToClipboard(setupData.manual_entry_key, 'manual')}
          >
            {copiedIndex === 'manual' ? (
              <Check className="w-4 h-4 text-emerald-500" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Avbryt
        </Button>
        <Button onClick={onNext}>
          Neste
        </Button>
      </DialogFooter>
    </div>
  );
}

function VerifyStep({ verifyCode, setVerifyCode, actionLoading, error, onBack, onVerify }) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="verify-code">Verifiseringskode</Label>
        <Input
          id="verify-code"
          placeholder="000 000"
          value={verifyCode}
          onChange={(e) => setVerifyCode(e.target.value)}
          className="text-center text-2xl tracking-widest"
          maxLength={7}
        />
        <p className="text-xs text-slate-500">
          Skriv inn den 6-sifrede koden fra autentiseringsappen din
        </p>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-sm text-red-600">
          {error}
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onBack}>
          Tilbake
        </Button>
        <Button 
          onClick={onVerify} 
          disabled={verifyCode.replace(/\s/g, '').length < 6 || actionLoading}
        >
          {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Verifiser
        </Button>
      </DialogFooter>
    </div>
  );
}

function BackupCodesStep({ backupCodes, copiedIndex, onCopyToClipboard, onCopyAllBackupCodes, onComplete }) {
  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <div className="flex items-center space-x-2 text-amber-700 mb-2">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-medium">Viktig!</span>
        </div>
        <p className="text-sm text-amber-600">
          Lagre disse kodene på et trygt sted. Du trenger dem hvis du mister tilgang til autentiseringsappen.
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-2">
        {backupCodes.map((code, i) => (
          <div 
            key={i}
            className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded font-mono text-sm"
          >
            <span>{code}</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => onCopyToClipboard(code, i)}
            >
              {copiedIndex === i ? (
                <Check className="w-3 h-3 text-emerald-500" />
              ) : (
                <Copy className="w-3 h-3" />
              )}
            </Button>
          </div>
        ))}
      </div>

      <Button
        variant="outline"
        className="w-full"
        onClick={onCopyAllBackupCodes}
      >
        {copiedIndex === 'all' ? (
          <>
            <Check className="w-4 h-4 mr-2 text-emerald-500" />
            Kopiert!
          </>
        ) : (
          <>
            <Copy className="w-4 h-4 mr-2" />
            Kopier alle koder
          </>
        )}
      </Button>

      <DialogFooter>
        <Button 
          onClick={onComplete}
          className="w-full bg-gradient-to-r from-teal-500 to-blue-600"
        >
          <Check className="w-4 h-4 mr-2" />
          Ferdig
        </Button>
      </DialogFooter>
    </div>
  );
}
